// 📄 pages/Admin/sections/ProgramaCatalogSection.tsx
import { useEffect, useState } from 'react';
import { Plus, RefreshCw, Code2 } from 'lucide-react';
import { Programa, getProgramas, deletePrograma } from '../../../services/catalogoProgramasService.ts';
import { BadiObjeto, getBadis } from '../../../services/catalogoBadisService.ts';
import { TransaccionSap, getTransacciones } from '../../../services/catalogoTransaccionesService.ts';
import ProgramaCatalogGrid from '../../../components/abap/ProgramaCatalogGrid.tsx';
import ProgramaFormModal from '../components/ProgramaFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

interface ProgramaCatalogSectionProps {
  onIrACatalogoBadis?: () => void;
  onIrACatalogoTransacciones?: () => void;
}

export default function ProgramaCatalogSection({ onIrACatalogoBadis, onIrACatalogoTransacciones }: ProgramaCatalogSectionProps) {
  const [programas, setProgramas] = useState<Programa[]>([]);
  const [badis, setBadis] = useState<BadiObjeto[]>([]);
  const [transacciones, setTransacciones] = useState<TransaccionSap[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPrograma, setEditingPrograma] = useState<Programa | null>(null);
  const [programaAEliminar, setProgramaAEliminar] = useState<Programa | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [prog, bad, trans] = await Promise.all([getProgramas(), getBadis(), getTransacciones()]);
      setProgramas(prog);
      setBadis(bad);
      setTransacciones(trans);
    } catch (err) {
      console.error('Error cargando el catálogo de Programas:', err);
      setError('No se pudo cargar el catálogo de Programas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditingPrograma(null); setModalOpen(true); };
  const openEdit = (programa: Programa) => { setEditingPrograma(programa); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!programaAEliminar) return;
    setEliminando(true);
    try {
      await deletePrograma(programaAEliminar.id);
      setProgramaAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar el programa:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Catálogo de Programas</h1>
          <p className="text-sm text-gray-400 mt-1">Programas e Includes ABAP (custom o estándar) usados en las integraciones SAP Ariba / S/4HANA, relacionados con BAdIs y Transacciones.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Nuevo Programa
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando catálogo...</p>
        </div>
      ) : programas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Code2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay programas registrados en el catálogo.</p>
        </div>
      ) : (
        <ProgramaCatalogGrid
          programas={programas}
          badis={badis}
          transacciones={transacciones}
          onEdit={openEdit}
          onDelete={setProgramaAEliminar}
          onVerEnBadis={onIrACatalogoBadis}
          onVerEnTransacciones={onIrACatalogoTransacciones}
        />
      )}

      {modalOpen && (
        <ProgramaFormModal programa={editingPrograma} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {programaAEliminar && (
        <ConfirmDialog
          title="¿Eliminar este programa del catálogo?"
          message={`"${programaAEliminar.nombre}" se eliminará del catálogo global. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setProgramaAEliminar(null)}
        />
      )}
    </div>
  );
}
